import * as React from "react";
import { Grid, GridCellProps, GridColumn as Column, GridFilterChangeEvent, GridToolbar, GridSortChangeEvent } from "@progress/kendo-react-grid";
import { DragAndDrop } from "@progress/kendo-react-common";
import { DraggableRow } from "./DraggableRow";
import { SelectionCell } from "./SelectionCell";
import DragHint from "./DragHint";
import GBKendoToolbar from "../../Shared/Components/Table/GBKendoToolbar";
import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import { PointReadTableDto } from "../../../Models/Point/PointReadTableDto";
import { CompositeFilterDescriptor, filterBy, orderBy, SortDescriptor } from "@progress/kendo-data-query";
import useKendoTableState from "../../Shared/Hooks/useKendoTableState";
import { useEffect, useMemo } from "react";
import { CustomTextFilterCell } from "../../Shared/Components/Table/CustomTextFilterCell";
import { DragHandleCell } from "./DragHandleCell";
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from "../../../messages/en-US";

interface Props {
  points: Array<PointReadTableDto>;
  updatePoints: (points: Array<PointReadTableDto>) => void;
  height: number;
}

const initialFilter: CompositeFilterDescriptor = {
  logic: "and",
  filters: [],
};

export const ReorderContext = React.createContext<{
  reorder: (dataItem: PointReadTableDto, direction: "before" | "after" | null) => void;
  dragStart: (dataItem: PointReadTableDto) => void;
  dragEnd: (dataItem?: PointReadTableDto) => void;
}>({ reorder: () => {}, dragStart: () => {}, dragEnd: () => {} });
export const SelectionContext = React.createContext<[PointReadTableDto[], any]>([[], () => {},]);
export const IsSelectedContext = React.createContext<boolean>(false);
export const DragHintContext = React.createContext<React.RefObject<HTMLElement> | null>(null);

interface KendoGridRef extends HTMLDivElement {
    element: HTMLDivElement;
}

export const GridContext = React.createContext<React.RefObject<KendoGridRef> | null>(null);
export var IsTableEditable = React.createContext<boolean>(false);

function RouteTable(props: Props) {

  const localizationService = useLocalization();

  const grid = React.useRef(null);
  const hint = React.useRef(null);
  const [selection, setSelection] = React.useState<PointReadTableDto[]>([]);
  const [activeItem, setActiveItem] = React.useState<PointReadTableDto | null>(null);

  const _export = React.useRef<ExcelExport | null>(null);
  const excelExport = () => {
    if (_export.current !== null) {
      _export.current.save();
    }
  };

  const updateAction = () => {
    props.updatePoints(tableState.assets);
  
  }

  const discardChanges = () => {
    // Reset the route order back to its initial state
    for(var a = 0; a < tableState.assets.length; a++)
    {
      tableState.assets[a].routeOrder = tableState.assets[a].initialRouteOrder;
    }
    tableState.resetState();
  }

  const [filter, setFilter] = React.useState(initialFilter);

  // Add sort state
  const [sort, setSort] = React.useState<Array<SortDescriptor>>([]);

  const tableState = useKendoTableState<PointReadTableDto>({
    key: "RouteEditor",
    initialAssets: props.points,
  });

  const [dragCounter, setDragCounter] = React.useState(0);

  const reorder = (dataItem: any, direction: "before" | "after" | null) => {
    if (activeItem === dataItem || !tableState.canModifyRow) {
        setSelection([]);
        return;
    }
 
    // Get all items in their current order with type safety
    const allItems = [...tableState.assets].sort((a, b) => {
        const aOrder = a.routeOrder ?? 0;  // Use 0 as fallback if undefined
        const bOrder = b.routeOrder ?? 0;
        return aOrder - bOrder;
    });
 
    // Find items being moved (selected items)
    const itemsToMove = selection;
 
    // Remove items being moved from the array
    const remainingItems = allItems.filter(
        item => !selection.some(selectedItem => selectedItem.id === item.id)
    );
 
    // Find target position in the full dataset
    const targetIndex = remainingItems.findIndex(p => p.id === dataItem.id);
    const insertIndex = direction === "before" ? targetIndex : targetIndex + 1;
 
    // Insert moved items at the target position
    remainingItems.splice(insertIndex, 0, ...itemsToMove);
 
    // Update route orders for ALL items
    const updatedTableStateAssets = [...tableState.assets];
    let ids: string[] = [];
 
    // Reassign route orders sequentially
    remainingItems.forEach((item, index) => {
        const assetIndex = updatedTableStateAssets.findIndex(x => x.id === item.id);
        if (assetIndex !== -1) {
            updatedTableStateAssets[assetIndex].routeOrder = index + 1;
            ids.push(item.id);
        }
    });
 
    tableState.setModifiedRowIds(ids);
    tableState.assets = updatedTableStateAssets;
    setSelection([]);
    setDragCounter(prev => prev + 1);
};

  const dragStart = (dataItem: any) => {
    setActiveItem(dataItem);
  };

  const dragEnd = () => {
    setActiveItem(null);
  };

  // Update processedData to handle both filtering and sorting
  const processedData = useMemo(() => {
    let initialRows = tableState.assets;

    // If filterable, then filter
    if (filter) {
      initialRows = filterBy(initialRows, filter);
    }

    // Apply current sort
    if (sort && sort.length > 0) {
      initialRows = orderBy(initialRows, sort);
    } else {
      // Fallback to default routeOrder sort
      initialRows = orderBy(initialRows, [{field: "routeOrder", dir: "asc"}]);
    }

    return initialRows;
  }, [tableState.assets, filter, sort, dragCounter]); // Add dragCounter to dependencies

  useEffect(() => {
   
    if (!tableState.canModifyRow)
    {
      // Clear all selected rows
      setSelection([]);
    }

  }, [tableState.canModifyRow]);

  const CustomSelectionCell = (props: GridCellProps) => (
    <SelectionCell {...props} isTableEditable={tableState.canModifyRow} />
  );

  const CustomDragHandleCell = (props: GridCellProps) => (
    <DragHandleCell {...props} isTableEditable={tableState.canModifyRow} />
  );

  return (
    <GridContext.Provider value={grid}>
      <DragHintContext.Provider value={hint}>
        <ReorderContext.Provider value={{ reorder, dragStart, dragEnd }}>
          <SelectionContext.Provider value={[selection, setSelection]}>
            <ExcelExport data={processedData} ref={_export} fileName={localizationService.toLanguageString('custom.routeEditor', enMessages.custom.routeEditor)}>
            <ExcelExportColumn field="routeOrder" title="Route Order" />
            <ExcelExportColumn field="name" title={localizationService.toLanguageString('custom.tagName', enMessages.custom.tagName)} />
            <ExcelExportColumn field="machineName" title={localizationService.toLanguageString('custom.asset', enMessages.custom.asset)} />
            <ExcelExportColumn field="areaName" title={localizationService.toLanguageString('custom.location', enMessages.custom.location)} />
            <ExcelExportColumn field="consumableName" title={localizationService.toLanguageString('custom.grease', enMessages.custom.grease)} />
            </ExcelExport>
            <DragAndDrop>
              <Grid
                style={{
                  height: props.height
                }}
                ref={grid}
                data={processedData}
                filterable={true}
                sortable={true}
                sort={sort}
                onSortChange={(e: GridSortChangeEvent) => setSort(e.sort)}
                rowHeight={46}
                pageSize={processedData.length}
                dataItemKey={"id"}
                filter={filter}
                total={processedData.length}
                onFilterChange={(e: GridFilterChangeEvent) => setFilter(e.filter)}
                rowRender={(row, rowProps) => (
                  <DraggableRow elementProps={row.props} {...rowProps} />
                )}
                scrollable={"scrollable"}
              >
                <GridToolbar>  
                  <GBKendoToolbar
                    name={localizationService.toLanguageString('custom.routeEditor', enMessages.custom.routeEditor)}
                    tableState={tableState}
                    exportAction={excelExport}
                    importAction={null}
                    addAction={null}
                    updateAction={updateAction}
                    deleteAction={null}
                    discardChanges={discardChanges}
                  />
                </GridToolbar>
            
                <Column field="" width="50px" cell={CustomSelectionCell} title="" filterable={false} sortable={false} />
                <Column field="" width="50px" cell={CustomDragHandleCell} title="" filterable={false} sortable={false} />
                <Column field="routeOrder" width="80px" title="#" filterable={false} sortable={true} />
                <Column field="name" title={localizationService.toLanguageString('custom.tagName', enMessages.custom.tagName)} 
                  filter="text" filterCell={CustomTextFilterCell} sortable={true} />
                <Column field="machineName" title={localizationService.toLanguageString('custom.asset', enMessages.custom.asset)} 
                  filter="text" filterCell={CustomTextFilterCell} sortable={true} />
                <Column field="areaName" title={localizationService.toLanguageString('custom.location', enMessages.custom.location)} 
                  filter="text" filterCell={CustomTextFilterCell} sortable={true} />
                <Column field="consumableName" title={localizationService.toLanguageString('custom.grease', enMessages.custom.grease)} 
                  filter="text" filterCell={CustomTextFilterCell} sortable={true} />
              </Grid>
              <DragHint
                ref={hint}
                portal={grid}
                className="k-card"
                style={{ display: activeItem ? undefined : "none" }}
              >
                {activeItem && activeItem.name}
                {selection.length > 1 && (
                  <div
                    style={{
                      position: "absolute",
                      pointerEvents: "none",
                      bottom: 0,
                      right: 0,
                      background: "red",
                      padding: 8,
                      width: 36,
                      color: "white",
                      borderRadius: "50%",
                      transform: "translate(50%, 50%)",
                    }}
                  >
                    +{selection.length - 1}
                  </div>
                )}
              </DragHint>
            </DragAndDrop>
            
          </SelectionContext.Provider>
        </ReorderContext.Provider>
      </DragHintContext.Provider>
    </GridContext.Provider>
  );
}

export default RouteTable;